import React, { useEffect, useState, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { ChevronRight, X, Hourglass } from 'lucide-react';
import specialtyService from '../services/specialtyService';
import { doctorService } from '../services/doctorService';
import { getScheduleByDateWithFallback } from '../services/scheduleService';
import { groupSlotsByDuration } from '../services/scheduleModels';
import { specialtyTheme } from '../utils/specialtyTheme';
import florLogo from '../assets/florDaisiesSF.png';

const SPECIALTY_ID = 16;
const SPECIALTY_NAME = 'Psicología';
const DURATION_ID = 109;
const TZ = 'America/Mexico_City';

// Fecha local YYYY-MM-DD (en-CA), evita el off-by-one de toISOString() en UTC.
const localYmd = (d = new Date()) => d.toLocaleDateString('en-CA');

const fmtTime = (t) => {
  if (!t) return '';
  const [h, m] = t.split(':');
  const hr = parseInt(h, 10);
  const ampm = hr >= 12 ? 'PM' : 'AM';
  return `${hr % 12 || 12}:${m} ${ampm}`;
};

const fmtDayLabel = (dateStr) => {
  const today = new Date();
  const todayStr = localYmd(today);
  const tomorrow = new Date(today);
  tomorrow.setDate(today.getDate() + 1);
  const tomorrowStr = localYmd(tomorrow);
  if (dateStr === todayStr) return 'hoy';
  if (dateStr === tomorrowStr) return 'mañana';
  const d = new Date(dateStr + 'T12:00:00');
  return d.toLocaleDateString('es-MX', { weekday: 'long', day: 'numeric', month: 'short' });
};

// Parseo del endpoint público (sin is_locked) como red de seguridad.
const slotsFromPublic = (res, durationId) => {
  const days = res?.result || res || [];
  if (!Array.isArray(days)) return [];
  return days.flatMap((d) =>
    (d.schedule_available || []).map((s) => ({
      id: s.id, scheduleId: s.id, init: s.init, end: s.end,
      professional_ids: s.professional_ids || [], date: d.date,
      duration_id: durationId, is_locked: s.is_locked === true, booking_mode: s.booking_mode,
    })),
  ).filter((s) => s.init && s.professional_ids.length > 0);
};

const byTime = (a, b) => {
  const ta = a.init.split(':').map(Number);
  const tb = b.init.split(':').map(Number);
  return (ta[0] * 60 + (ta[1] || 0)) - (tb[0] * 60 + (tb[1] || 0));
};

// Igual que CalendarSelection (flujo "agendar por fecha"): los slots atómicos del
// endpoint v2 se AGRUPAN por duración con groupSlotsByDuration; los slots agrupados
// traen is_locked (garantizado) y booking_mode reales.
const slotsFromDays = (result, durationId) => {
  const targetMin = result?.slotDurationMinutes || 60;
  const atomicMin = result?.slotDurationMinutes || targetMin;
  const out = [];
  (result?.days || []).forEach((day) => {
    const booking = groupSlotsByDuration({
      slots: day.slots || [],
      targetDurationMinutes: targetMin,
      atomicSlotMinutes: atomicMin,
    });
    booking.forEach((bs) => {
      if (bs.isOccupied) return;
      if (!bs.hourStart) return;
      if (!(bs.professionalIds || []).length) return;
      out.push({
        id: bs.slotId,
        scheduleId: bs.slotId,
        init: bs.hourStart,
        end: bs.hourStop,
        professional_ids: bs.professionalIds,
        date: day.date,
        duration_id: durationId,
        is_locked: bs.isLocked,
        booking_mode: bs.bookingMode,
      });
    });
  });
  return out;
};

/**
 * Grid 2x1 (fondo blanco, sin iconos decorativos):
 *  1) Horario garantizado más próximo de Psicología (énfasis especialidad + doctora).
 *  2) "Mostrar lo disponible hoy" → modal con todas las especialidades de hoy,
 *     diferenciando garantizados (is_locked) de los de confirmación.
 * Usa el endpoint autenticado (v2) — requiere userId + authToken — para tener is_locked real.
 */
const NearestSlotCard = ({ onStartBooking, userId, authToken }) => {
  const [slot, setSlot] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [groups, setGroups] = useState(null);
  const [loadingToday, setLoadingToday] = useState(false);

  // Slots de una especialidad para una fecha. Primero el endpoint autenticado v2
  // (trae is_locked → diferenciación); si no devuelve nada, cae al público (sin
  // is_locked, pero al menos muestra los horarios).
  const getSpecSlots = useCallback(async (specId, durationId, dateStr) => {
    try {
      const ws = await getScheduleByDateWithFallback({ specialtyId: specId, durationId, userId, authToken, date: dateStr, tz: TZ });
      const s = slotsFromDays(ws, durationId);
      if (s.length > 0) return s;
    } catch (_) { /* fall through */ }
    try {
      const res = await doctorService.getAvailabilityByDatePublic(specId, durationId, dateStr, false, TZ);
      return slotsFromPublic(res, durationId);
    } catch (_) {
      return [];
    }
  }, [userId, authToken]);

  // ── Horario garantizado más próximo de Psicología (tile 1) ──
  useEffect(() => {
    let cancelled = false;
    const findNearest = async () => {
      const today = new Date();
      for (let i = 0; i < 5; i++) {
        if (cancelled) return;
        const d = new Date(today);
        d.setDate(today.getDate() + i);
        const dateStr = localYmd(d);
        try {
          let slots = await getSpecSlots(SPECIALTY_ID, DURATION_ID, dateStr);
          const locked = slots.filter((s) => s.is_locked === true);
          if (locked.length > 0) slots = locked;
          if (slots.length === 0) continue;
          slots.sort((a, b) => (a.date !== b.date ? (a.date < b.date ? -1 : 1) : byTime(a, b)));
          if (!cancelled) setSlot(slots[0]);
          return;
        } catch (_) { /* next day */ }
      }
    };
    findNearest();
    return () => { cancelled = true; };
  }, [getSpecSlots]);

  // ── Disponibilidad de HOY, todas las especialidades (modal) ──
  const loadToday = useCallback(async () => {
    setLoadingToday(true);
    try {
      const specs = await specialtyService.getFormattedOnDemandSpecialties();
      const active = (specs || []).filter((s) => s.active !== false && (s.durationId || s.extraDurationId));
      const todayStr = localYmd();
      const results = await Promise.all(
        active.map(async (sp) => {
          const durationId = sp.durationId || sp.extraDurationId;
          const all = await getSpecSlots(sp.id, durationId, todayStr);
          const slots = all.filter((s) => s.date === todayStr).sort(byTime);
          return { spec: sp, slots };
        }),
      );
      setGroups(results.filter((r) => r.slots.length > 0));
    } catch (_) {
      setGroups([]);
    } finally {
      setLoadingToday(false);
    }
  }, [getSpecSlots]);

  const openModal = () => {
    setModalOpen(true);
    if (groups === null) loadToday();
  };

  useEffect(() => {
    if (!modalOpen) return;
    const onKey = (e) => { if (e.key === 'Escape') setModalOpen(false); };
    document.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
    };
  }, [modalOpen]);

  const bookSlot = (specId, s) => {
    setModalOpen(false);
    onStartBooking(specId, { ...s, time: s.init, doctor: s.professional_ids[0] || {} });
  };

  const psicoDoctor = slot?.professional_ids?.[0] || {};
  const psicoName = psicoDoctor.name ? `${psicoDoctor.name} ${psicoDoctor.last_name || ''}`.trim() : 'una doctora';
  const psicoAvatar = psicoDoctor.avatar || `https://ui-avatars.com/api/?name=${encodeURIComponent(psicoName)}&background=786ED9&color=fff&size=96`;

  return (
    <>
      <div className="grid grid-cols-2 gap-3 items-stretch">
        {/* Tile 1 — horario garantizado más próximo de Psicología */}
        <button
          type="button"
          onClick={() => onStartBooking(SPECIALTY_ID, slot ? { ...slot, time: slot.init, doctor: psicoDoctor } : undefined)}
          className="press-scale-card h-full bg-surface-card rounded-card p-5 text-left flex flex-col transition-transform"
        >
          <div className="flex items-center gap-2.5 mb-3">
            <img
              src={psicoAvatar}
              alt={psicoName}
              className="w-11 h-11 rounded-full object-cover ring-2 ring-[#ffe1d6] shrink-0"
              onError={(e) => { e.target.src = `https://ui-avatars.com/api/?name=D&background=786ED9&color=fff`; }}
            />
            <span className="text-[13px] font-bold text-[#5a2410] leading-tight">{SPECIALTY_NAME}</span>
          </div>
          {slot ? (
            <>
              <p className="text-[12px] text-text-mute leading-snug">El horario más próximo es:</p>
              <p className="font-bold text-brand-deep text-[clamp(1rem,4.2vw,1.2rem)] leading-tight tracking-[-0.01em] mt-0.5">
                {fmtDayLabel(slot.date)} a las {fmtTime(slot.init)}
              </p>
              <p className="text-[12px] text-text-body mt-auto pt-2 truncate">{psicoName}</p>
            </>
          ) : (
            <p className="font-semibold text-brand-deep text-[14px]">Ver horarios disponibles</p>
          )}
        </button>

        {/* Tile 2 — mostrar lo disponible hoy (abre modal) */}
        <button
          type="button"
          onClick={openModal}
          className="press-scale-card h-full bg-surface-card rounded-card p-5 text-left flex flex-col transition-transform"
        >
          <p className="font-bold text-brand-deep text-[clamp(1rem,4.2vw,1.2rem)] leading-tight tracking-[-0.01em]">
            Mostrar lo disponible hoy
          </p>
          <p className="text-[12.5px] text-text-mute mt-1.5 leading-snug">Todas las especialidades con horario hoy</p>
          <span className="mt-auto pt-3 inline-flex items-center gap-1 text-brand text-[13px] font-semibold">
            Ver horarios
            <ChevronRight size={15} aria-hidden="true" />
          </span>
        </button>
      </div>

      {/* Modal vía portal a document.body — escapa de cualquier ancestro con transform */}
      {modalOpen && createPortal(
        <div
          className="fixed inset-0 z-[80] flex items-end md:items-center justify-center p-0 md:p-4"
          role="dialog"
          aria-modal="true"
          aria-label="Disponible hoy"
        >
          <button
            type="button"
            tabIndex={-1}
            aria-hidden="true"
            onClick={() => setModalOpen(false)}
            className="absolute inset-0 bg-brand-deep/55 backdrop-blur-sm"
          />
          <div className="relative bg-warm-canvas w-full md:max-w-lg rounded-t-[28px] md:rounded-[24px] max-h-[85dvh] flex flex-col overflow-hidden">
            <div className="px-5 pt-5 pb-3 shrink-0">
              <div className="flex items-start justify-between">
                <div>
                  <h3 className="text-lg font-bold text-brand-deep tracking-[-0.015em]">Disponible hoy</h3>
                  <p className="text-[12.5px] text-text-mute">Horarios de todas las especialidades para hoy</p>
                </div>
                <button
                  type="button"
                  onClick={() => setModalOpen(false)}
                  aria-label="Cerrar"
                  className="press-scale shrink-0 w-9 h-9 rounded-full bg-brand-tint text-brand-deep flex items-center justify-center hover:bg-brand hover:text-white transition-colors"
                >
                  <X size={18} aria-hidden="true" />
                </button>
              </div>
              {/* Leyenda garantizado vs por confirmación */}
              <div className="flex items-center gap-4 mt-3 text-[11.5px] text-brand-deep/75">
                <span className="inline-flex items-center gap-1.5">
                  <span className="w-3.5 h-3.5 rounded bg-brand" aria-hidden="true" />
                  Garantizado
                </span>
                <span className="inline-flex items-center gap-1.5">
                  <span className="inline-flex items-center justify-center w-3.5 h-3.5 rounded bg-amber-pill/60" aria-hidden="true">
                    <Hourglass size={9} className="text-amber-deep" />
                  </span>
                  Por confirmación
                </span>
              </div>
            </div>

            <div className="overflow-y-auto min-h-0 flex-1 px-5 pb-6 overscroll-contain">
              {loadingToday || groups === null ? (
                <div className="py-12 flex flex-col items-center justify-center">
                  <img src={florLogo} alt="Cargando" className="h-10 w-10 animate-spin-slow" />
                  <p className="mt-3 text-sm text-brand-deep/70 font-medium">Buscando horarios de hoy...</p>
                </div>
              ) : groups.length === 0 ? (
                <div className="py-12 text-center">
                  <p className="text-brand-deep font-semibold">No hay horarios disponibles hoy</p>
                  <p className="text-text-mute text-sm mt-1">Revisa los próximos días desde el explorador de horarios.</p>
                </div>
              ) : (
                <div className="space-y-5">
                  {groups.map(({ spec, slots }) => {
                    const theme = specialtyTheme(spec.name);
                    return (
                      <div key={spec.id}>
                        <div className="flex items-center gap-2 mb-2">
                          <span className="flex items-center justify-center w-7 h-7 rounded-full bg-white/70" style={{ color: theme.iconColor }}>
                            <theme.Icon size={15} aria-hidden="true" />
                          </span>
                          <h4 className="font-bold text-brand-deep text-sm">{spec.name}</h4>
                          <span className="ml-auto text-[11px] text-text-mute">{slots.length} {slots.length === 1 ? 'horario' : 'horarios'}</span>
                        </div>
                        <div className="flex flex-wrap gap-2">
                          {slots.map((s, i) => {
                            const doc = s.professional_ids[0];
                            const guaranteed = s.is_locked === true;
                            return (
                              <button
                                key={`${s.id}-${s.init}-${i}`}
                                type="button"
                                onClick={() => bookSlot(spec.id, s)}
                                title={guaranteed ? 'Horario garantizado' : 'Por confirmación'}
                                className={`press-scale inline-flex flex-col items-center gap-0.5 rounded-card-sm px-3 py-1.5 transition-colors ${
                                  guaranteed
                                    ? 'bg-brand-tint text-brand-deep hover:bg-brand hover:text-white'
                                    : 'bg-amber-pill/25 text-amber-deep hover:bg-amber-pill/45'
                                }`}
                              >
                                <span className="inline-flex items-center gap-1 font-bold text-[12.5px]">
                                  {!guaranteed && <Hourglass size={10} aria-hidden="true" />}
                                  {fmtTime(s.init)}
                                </span>
                                {doc?.name && (
                                  <span className="text-[9px] font-semibold opacity-80 leading-none max-w-[72px] truncate">{doc.name}</span>
                                )}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>,
        document.body,
      )}
    </>
  );
};

export default NearestSlotCard;
